import React, { FC, useState } from "react";
import UsersOnlineStatus from "../../../../UsersOnlineStatus";
import { abbreviateNumber } from "../../../Trades/CreateTrade/helpers";
import Switch from "react-switch";
import { getRating, getTotalRating } from "../helpers";
import { OfferType, ReviewType } from "../Types";
import { t } from "../../../../../utils/i18n";

interface Props {
  hit: OfferType;
  currentExternalProfileId: string;
  changeOfferStatus: (offer: OfferType, status: boolean) => void;
  reviews: ReviewType[];
}

const HitComponent: FC<Props> = ({
  hit,
  currentExternalProfileId,
  changeOfferStatus,
  reviews,
}) => {
  const [expandedOffers, setExpandedOffers] = useState([]);

  if (!hit || !hit.cryptoCurrency || !hit.fiatCurrency || !hit.paymentMethod) {
    console.error("Incomplete data received:", { hit });

    return <div></div>;
  }

  let tags = [];
  try {
    tags = hit.tags ? JSON.parse(hit.tags) : [];
  } catch (e) {
    console.error("Failed to parse tags JSON:", e);
    tags = [];
  }

  const toggleDetails = (index) => {
    setExpandedOffers((prevExpandedOffers) => {
      const newExpandedOffers = [...prevExpandedOffers];
      if (newExpandedOffers.includes(index)) {
        newExpandedOffers.splice(newExpandedOffers.indexOf(index), 1);
      } else {
        newExpandedOffers.push(index);
      }
      return newExpandedOffers;
    });
  };

  const calculateMinUSD = () => {
    const tradeRate = Number(hit.tradeRate);
    const rangeMin = Number(hit.rangeMin);
  
    const result = tradeRate !== 0 ? rangeMin / tradeRate : 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(result || 0);
  };
  
  const calculateMaxUSD = () => {
    const tradeRate = Number(hit.tradeRate);
    const rangeMax = Number(hit.rangeMax);
  
    const result = tradeRate !== 0 ? rangeMax / tradeRate : 0;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(result || 0);
  };

  const getInitialLetter = () => {
    return hit.customOfferLabel
      ? hit.customOfferLabel.charAt(0).toUpperCase()
      : "";
  };

  return (
    <div className="hit-content">
      <div className="table-container">
        <table className="table table-hover offer-list-table">
          <tbody className="offer-list-table-body">
            <tr id={`offer_${hit.id}`} className="offer-list-table-body-row">
              <td className="profilesection">
                <div className="d-flex profile-head">
                  <div
                    className="avatar-default"
                    style={{
                      backgroundColor:
                        hit.offerTypeField === "sell"
                          ? "#4c3f84dc"
                          : "#5256a3a6;",
                    }}
                  >
                    {getInitialLetter()}
                  </div>
                  <div className="offer_name pl-2 d-block">
                    {currentExternalProfileId === hit.profile?.id && (
                      <div className="profile-myoffer">
                        <i className="fa fa-star"></i> {t("p2p.hit.my_offer")}
                      </div>
                    )}
                    <div
                      className="offer-display-nickname"
                      title={`${hit?.customOfferLabel}`}
                    >
                      <a
                        href={`/profile/${hit?.profile?.userName}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {hit?.customOfferLabel.length > 15
                          ? `${hit?.customOfferLabel.slice(0, 15)}...`
                          : `${hit?.customOfferLabel.slice(0, 15)}`}{" "}
                      </a>
                      &nbsp;
                      <i
                        title={t("p2p.hit.verified")}
                        className="fa fa-check-circle"
                        style={{ color: "#daf7a6" }}
                      ></i>
                    </div>
                    <div className="font-0_3">
                      <UsersOnlineStatus userId={hit?.profile?.userId} />
                    </div>
                    <div className="profile-more-details">
                      <>
                        <span title={t("p2p.hit.total_trades_tip")}>
                          {t("p2p.hit.trades", { count: abbreviateNumber(hit?.profile?.totalTrades || 0) })}
                        </span>
                        <span className="offer-profiledetails-divider">
                          &nbsp;|&nbsp;
                        </span>
                        {/* <div>Trade Volume: {hit?.profile?.tradeVolume}</div> */}
                        {/* <span>{hit?.profile?.trustedByCount} Reviews </span> */}
                        <span title={t("p2p.hit.rating_tip")}>
                          <i className="fa fa-thumbs-up profiledetails-icon"></i>
                          &nbsp;
                          {reviews
                            ? `${getRating(hit?.profile?.id, reviews)}`
                            : "..."}
                          <span className="offer-profiledetails-divider">
                          <br/>
                          </span>
                        </span>

                        <span title={t("p2p.hit.reviews_tip")}>
                        <i className="fa fa-comments profiledetails-icon"></i> {t("p2p.hit.reviews")} 
                          &nbsp;
                          {reviews
                            ? `${getTotalRating(hit?.profile?.id, reviews)} `
                            : "..."}
                          <span className="offer-profiledetails-divider">
                            <br/>
                          </span>
                        </span>

                        <span title={t("p2p.hit.time_tip")}>
                          <i className="fa fa-stopwatch profiledetails-icon"></i>{" "}
                          {t("p2p.hit.thirty")}
                        </span>
                      </>
                    </div>
                  </div>
                </div>
              </td>
              <td
                data-label={t("p2p.hit.price_label")}
                title={t("p2p.hit.rate_tip")}
                className="offers-table-cols"
              >
                <div className="offer_price">
                  <span className="offerprice">
                    <span>
                      {new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(parseFloat(hit.tradeRate))}
                      <sub>
                        {hit.cryptoCurrency.code == "USDT"
                          ? ""
                          : `1 ${hit.cryptoCurrency.code} =`}{" "}
                        {hit.fiatCurrency.code}{" "}
                      </sub>
                    </span>{" "}
                  </span>{" "}
                  <br />
                </div>
              </td>
              <td data-label={t("p2p.hit.available_label")} className="offers-table-cols">
                <div className="offer_price ">
                  <span
                    title={t("p2p.hit.available_tip")}
                    className="font-weight-bold font-1_2 offer_price-usd"
                  >
                    {new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    }).format(parseFloat(hit.availableAmount || "0"))}
                    <sub>USD </sub>
                  </span>
                  <br />
                  {hit.rangeMin > 0 && hit.rangeMin > 0 && (
                    <div title={t("p2p.hit.minmax_tip")}>
                      <span className="offerslist-minmax">
                        <span>{t("p2p.hit.min")}&nbsp;</span>
                        {new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(hit.rangeMin)}{" "}
                        {hit.fiatCurrency.code}
                        {" "}
                        (
                          {calculateMinUSD()}
                        )
                      </span>
                      <br />
                      <span className="offerslist-minmax">
                        <span>{t("p2p.hit.max")}&nbsp;</span>
                        {new Intl.NumberFormat("en-US", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(hit.rangeMax)}{" "}
                        {hit.fiatCurrency.code}
                        {" "}
                        (
                          {calculateMaxUSD()}
                        )
                      </span>
                    </div>
                  )}
                </div>
              </td>
              <td data-label={t("p2p.hit.payment_label")} className="offers-table-cols">
                <div className="offer_paymentmethod">
                  <p className="avatar-offer"></p>
                  <p className="offer_paymentmethod-name">
                    {hit.paymentMethod.name}
                  </p>
                </div>
              </td>
              <td className="offers-table-cols">
                <div className="offer_actions d-flex pl-2">
                  {currentExternalProfileId !== hit.profile?.id && (
                    <div className="hit-action">
                      <a
                        href={`/p2p_trades/offers/trade/${hit.id}/create`}
                        className={`btn offers-actionbtns ${
                          hit.offerTypeField === "buy" ? "sell-btn" : "buy-btn"
                        } float-right`}
                      >
                        {t(
                          hit.offerTypeField === "buy" ? "p2p.hit.sell_token" : "p2p.hit.buy_token",
                          {
                            token:
                              hit.cryptoCurrency.code === "USDT"
                                ? "USD"
                                : hit.cryptoCurrency.code,
                          }
                        )}
                      </a>
                    </div>
                  )}
                  {currentExternalProfileId === hit.profile?.id && (
                    <div className="my-offer-action">
                      <label className="myoffer-status-cont">
                        <span>{hit.active ? t("p2p.hit.active") : t("p2p.hit.inactive")}</span>
                        <br />
                        <Switch
                          onChange={(e) => changeOfferStatus(hit, e)}
                          className="react-switch"
                          checked={hit.active}
                          disabled={!hit.canDeactivate}
                        />
                      </label>
                      <a
                        href={`/p2p_trades/offers/${hit.id}/edit`}
                        className="myoffers-edit"
                      >
                        <span>
                          <i className="fa fa-pencil"></i> {t("p2p.hit.edit")}
                        </span>
                      </a>{" "}
                    </div>
                  )}
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HitComponent;
